import React from "react";
import {
  MdAutoAwesome,
  MdCalendarMonth,
  MdCelebration,
  MdEventAvailable,
  MdOutlineWbSunny,
  MdSchedule,
} from "react-icons/md";
import {
  getDaysUntilEnrollmentGoal,
  getEnrollmentGoalProgress,
} from "./enrollmentGoalUtils";
import "./enrollment-goal-banner.css";

const EnrollmentMetric = ({ icon: Icon, label, value, detail, tone }) => (
  <div className={`enrollment-goal-banner__metric is-${tone}`}>
    <span className="enrollment-goal-banner__metric-icon" aria-hidden="true">
      <Icon />
    </span>
    <span className="enrollment-goal-banner__metric-copy">
      <strong>{value}</strong>
      <span>{label}</span>
      <small>{detail}</small>
    </span>
  </div>
);

const EnrollmentGoalBanner = ({
  families = [],
  cards = [],
  studentsWithLessons,
  loading = false,
  compact = false,
}) => {
  const progress = React.useMemo(
    () => getEnrollmentGoalProgress({ families, cards, studentsWithLessons }),
    [cards, families, studentsWithLessons]
  );
  const daysRemaining = getDaysUntilEnrollmentGoal();
  const pipelineStudents =
    progress.summerBreakStudents +
    progress.onboardingStudents +
    progress.introStudents +
    progress.schedulingStudents;
  const pipelineWidth = Math.max(
    progress.projectedPercent - progress.enrolledPercent,
    0
  );
  const goalReached = progress.enrolledStudents >= progress.goal;

  return (
    <section
      className={`enrollment-goal-banner ${compact ? "is-compact" : ""} ${goalReached ? "is-complete" : ""}`}
      aria-labelledby="enrollment-goal-title"
    >
      <div className="enrollment-goal-banner__sparkle sparkle-one" aria-hidden="true">
        <MdAutoAwesome />
      </div>
      <div className="enrollment-goal-banner__sparkle sparkle-two" aria-hidden="true">
        <MdAutoAwesome />
      </div>

      <div className="enrollment-goal-banner__headline">
        <div className="enrollment-goal-banner__eyebrow">
          <MdCelebration aria-hidden="true" />
          All-school enrollment goal
        </div>
        <div className="enrollment-goal-banner__title-row">
          <div>
            <h2 id="enrollment-goal-title">
              {goalReached ? "We reached 300!" : "Road to 300"}
            </h2>
            <p>300 enrolled students by October 1, 2026</p>
          </div>
          <div className="enrollment-goal-banner__deadline">
            <MdCalendarMonth aria-hidden="true" />
            <span>
              <strong>{daysRemaining}</strong>
              days to go
            </span>
          </div>
        </div>
      </div>

      <div className="enrollment-goal-banner__score" aria-live="polite">
        {loading ? (
          <div className="enrollment-goal-banner__loading">Updating progress…</div>
        ) : (
          <>
            <div className="enrollment-goal-banner__score-number">
              {progress.enrolledStudents}
              <span>/ {progress.goal}</span>
            </div>
            <div className="enrollment-goal-banner__score-label">
              with lessons this month
            </div>
            <div className="enrollment-goal-banner__remaining">
              {goalReached
                ? "Goal complete — amazing work!"
                : `${progress.remainingStudents} students to go`}
            </div>
          </>
        )}
      </div>

      <div className="enrollment-goal-banner__metrics">
        <EnrollmentMetric
          icon={MdOutlineWbSunny}
          label="Summer break"
          value={progress.summerBreakStudents}
          detail="return not confirmed"
          tone="summer"
        />
        <EnrollmentMetric
          icon={MdEventAvailable}
          label="Intro lessons"
          value={progress.introStudents}
          detail="set up"
          tone="intro"
        />
        <EnrollmentMetric
          icon={MdSchedule}
          label="Scheduling"
          value={progress.schedulingStudents}
          detail="new students"
          tone="scheduling"
        />
      </div>

      <div className="enrollment-goal-banner__progress-area">
        <div className="enrollment-goal-banner__progress-copy">
          <span>{Math.round(progress.enrolledPercent)}% of goal enrolled</span>
          <span>
            {pipelineStudents > 0
              ? `${progress.projectedEnrollment} possible with pipeline + returns`
              : "New pipeline students will appear here"}
          </span>
        </div>
        <div
          className="enrollment-goal-banner__progress-track"
          role="progressbar"
          aria-label="Enrollment progress toward 300 students"
          aria-valuemin="0"
          aria-valuemax={progress.goal}
          aria-valuenow={Math.min(progress.enrolledStudents, progress.goal)}
        >
          <div
            className="enrollment-goal-banner__progress-enrolled"
            style={{ width: `${progress.enrolledPercent}%` }}
          />
          <div
            className="enrollment-goal-banner__progress-pipeline"
            style={{
              left: `${progress.enrolledPercent}%`,
              width: `${pipelineWidth}%`,
            }}
          />
        </div>
        {progress.onboardingStudents > 0 && (
          <div className="enrollment-goal-banner__pipeline-note">
            Projected total also includes {progress.onboardingStudents} student
            {progress.onboardingStudents === 1 ? "" : "s"} already onboarding.
          </div>
        )}
      </div>
    </section>
  );
};

export default EnrollmentGoalBanner;
